import { useEffect, useRef } from 'react';
import { PRESET_GROUP_TYPES, AVAILABLE_SHORTCUTS, baseShortcuts, gameShortcuts, designShortcuts, devShortcuts, aiEngines } from '../constants/presets';
import { getIconName } from '../constants/icons';

export const useAutoBackup = (uid, menuItems, pageData, settings, searchHistory) => {
  const cloudTimeoutRef = useRef(null);
  const pendingPayloadRef = useRef(null);
  const lastDataStringRef = useRef(null);

  if (lastDataStringRef.current === null) {
    try {
      const saved = localStorage.getItem(`tab365_visitor_backup_${uid}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.data) {
          lastDataStringRef.current = JSON.stringify(parsed.data);
        }
      }
    } catch(e) {}
  }

  const uploadToCloud = (payload) => {
    const token = localStorage.getItem('tab365_token');
    if (token && payload) {
      fetch('/api/v1/sync/backup/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        keepalive: true,
        body: JSON.stringify(payload)
      }).catch(err => console.error('Failed to backup to cloud', err));
      
      pendingPayloadRef.current = null;
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pendingPayloadRef.current) {
        uploadToCloud(pendingPayloadRef.current);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (cloudTimeoutRef.current) {
        clearTimeout(cloudTimeoutRef.current);
      }
      if (pendingPayloadRef.current) {
        uploadToCloud(pendingPayloadRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!menuItems || !pageData || !settings || !searchHistory) return;
    
    const timeout = setTimeout(() => {
      const serializedMenuItems = menuItems.map(m => ({ id: m.id, title: m.title, iconName: getIconName(m.icon) }));
      
      const allPresets = [
        ...PRESET_GROUP_TYPES.flatMap(g => g.shortcuts),
        ...AVAILABLE_SHORTCUTS.flatMap(c => c.items),
        ...baseShortcuts,
        ...gameShortcuts,
        ...designShortcuts,
        ...devShortcuts,
        ...aiEngines
      ];
      
      const serializedPageData = {};
      Object.keys(pageData).forEach(pageId => {
        serializedPageData[pageId] = pageData[pageId].map(item => {
          if (item.type === 'widget') {
            return { id: item.id, type: 'widget', widgetType: item.widgetType, w: item.w, h: item.h };
          } else {
            return { 
              ...item, 
              icon: typeof item.icon === 'string' ? item.icon : undefined,
              iconName: item.iconName || (typeof item.icon === 'function' || typeof item.icon === 'object' ? getIconName(item.icon) : undefined)
            };
          }
        });
      });
      
      const currentData = {
        menuItems: serializedMenuItems,
        pageData: serializedPageData,
        settings: settings,
        searchHistory: searchHistory
      };
      
      const currentDataStr = JSON.stringify(currentData);
      if (lastDataStringRef.current === currentDataStr) {
        return; // 数据没有实质性改变，跳过备份！
      }
      lastDataStringRef.current = currentDataStr;
      
      const payload = {
        uid,
        timestamp: Date.now(),
        data: currentData
      };
      
      // 1. 本地极速备份 (500ms防抖)
      localStorage.setItem(`tab365_visitor_backup_${uid}`, JSON.stringify(payload));
      
      // 2. 云端空闲备份 (10秒防抖)
      pendingPayloadRef.current = payload;
      if (cloudTimeoutRef.current) {
        clearTimeout(cloudTimeoutRef.current);
      }
      cloudTimeoutRef.current = setTimeout(() => {
        if (pendingPayloadRef.current) {
          uploadToCloud(pendingPayloadRef.current);
        }
      }, 10000);
      
    }, 500); // 监听变动 500ms
    
    return () => clearTimeout(timeout);
  }, [uid, menuItems, pageData, settings, searchHistory]);
};
